'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import Image from 'next/image';
import { ReactNode } from 'react';

import { useLDFlags } from '@/analytics';
import { CallButton } from '@/app/components';
import { Eyebrow } from '@/app/home-solar/components';
import { ButtonLink, ColumnSection, H2, H3, NumberedStep, Section } from '@/app/lp/components';
import { TRACKING } from '@/helpers/tracking';

import heroNeighborhood from './hero-neighborhood.webp';
import relayHome from './relay-home.webp';
import { PROCESS_PHASES, ProcessPhase } from './steps-data';
import supportCrew from './support-crew.webp';

const AURORA_PHASE = PROCESS_PHASES[0];
const INSTALLER_PHASE = PROCESS_PHASES[1];

const GetEstimatesButton = ({ children, variant = 'cta' }: { children: ReactNode; variant?: 'cta' | 'primary' }) => (
  <ButtonLink
    event={{ name: TRACKING.LANDING_CLICK_GET_QUOTE }}
    icon={DS.IconArrowRight}
    iconPosition="right"
    path="/quote-builder"
    size={{ _: 'sm', md: 'md' }}
    variant={variant}
  >
    {children}
  </ButtonLink>
);

const PhoneSupportCallout = () => {
  const { communicationUnavailable } = useLDFlags();

  if (communicationUnavailable) {
    return null;
  }

  return (
    <>
      <CallButton variant="primary" />
      <DS.Text color="uiHelperCopy" text="body14">
        Phone support weekdays, 9 AM to 5 PM MT
      </DS.Text>
    </>
  );
};

const CheckItem = ({ children }: { children: ReactNode }) => (
  <DS.Flex alignItems="flex-start" flexDirection="row" gap={2}>
    <DS.IconCheckCircle color="uiCard.successText" flexShrink={0} size={4} />
    <DS.Text flex={1} text="body16">
      {children}
    </DS.Text>
  </DS.Flex>
);

const PhaseSteps = ({ offset, phase }: { offset: number; phase: ProcessPhase }) => (
  <Section bg={phase.id === 'with-aurora' ? 'uiBgAlternate' : undefined}>
    <Eyebrow color="purple-400">{phase.label}</Eyebrow>
    <H2>{phase.heading}</H2>
    <DS.Text color="uiHelperCopy" maxW="760px" text="body16">
      {phase.intro}
    </DS.Text>
    <DS.Flex gap={7} pt={4}>
      {phase.steps.map((step, index) => {
        const n = offset + index + 1;
        return (
          <DS.Flex
            borderTop="1px solid"
            borderTopColor="uiDivider"
            flexDirection={{ _: 'column', md: 'row' }}
            gap={{ _: 4, md: 6 }}
            id={`step-${n}`}
            key={step.name}
            pt={6}
          >
            <NumberedStep>{n}</NumberedStep>
            <DS.Flex flex={1} gap={3}>
              <H3>{step.name}</H3>
              {step.body.map((paragraph) => (
                <p key={paragraph.slice(0, 48)}>{paragraph}</p>
              ))}
            </DS.Flex>
            <DS.Flex
              bg={phase.id === 'with-aurora' ? 'white' : 'uiBgAlternate'}
              border="1px solid"
              borderColor="uiDivider"
              borderRadius={8}
              flex={{ md: '0 0 280px' }}
              gap={2}
              p={5}
            >
              <DS.Text fontWeight={600} text="body14">
                {step.whyLabel}
              </DS.Text>
              <DS.Text color="uiHelperCopy" text="body14">
                {step.why}
              </DS.Text>
            </DS.Flex>
          </DS.Flex>
        );
      })}
    </DS.Flex>
  </Section>
);

export const HowToGoSolarPage = () => (
  <article>
    <DS.DarkModeProvider>
      <x.section bg="black" mt={-60} px={{ _: 6, lg: 10 }}>
        <DS.Flex
          alignItems="center"
          flexDirection={{ _: 'column', lg: 'row' }}
          gap={{ _: 8, lg: 10 }}
          maxW={DS.BorealisTheme.screens.xl}
          mx="auto"
          pb={{ _: 8, lg: 10 }}
          pt={{ _: 110, lg: 130 }}
        >
          <DS.Flex flex={{ lg: '3 1 0' }} gap={5}>
            <Eyebrow color="yellow-200">How it works</Eyebrow>
            <DS.Text as="h1" text={{ _: 'h2', lg: 'd3', md: 'd1' }}>
              Your solar timeline, start to finish
            </DS.Text>
            <DS.Text text={{ _: 'body16', md: 'body20' }}>
              Aurora is on your side. From your first estimate to the day your system switches on, we&rsquo;ll help you
              make sense of every step.
            </DS.Text>
            <GetEstimatesButton>Start with your estimate</GetEstimatesButton>
          </DS.Flex>
          <x.div
            borderRadius={16}
            flex={{ lg: '2 1 0' }}
            h={{ _: 240, lg: 333, md: 320 }}
            maxW={{ lg: '484px' }}
            overflow="hidden"
            position="relative"
            w="100%"
          >
            <Image
              alt="Aerial view of a neighborhood at sunset with solar panels on several rooftops"
              fill
              priority
              sizes="(max-width: 1080px) 100vw, 484px"
              src={heroNeighborhood}
              style={{ objectFit: 'cover' }}
            />
          </x.div>
        </DS.Flex>
      </x.section>
    </DS.DarkModeProvider>

    <PhaseSteps offset={0} phase={AURORA_PHASE} />

    <Section
      colorMode="dark"
      imageRight={{ alt: 'Home with rooftop solar panels surrounded by a lush garden', height: 420, src: relayHome }}
    >
      <H2>This isn&rsquo;t a handoff. It&rsquo;s a relay.</H2>
      <p>
        When you connect, your chosen installer receives the full picture, and most of the industry&rsquo;s top
        installers run their own detailed design work on Aurora software. You used our technology to shop; they use it
        to build. They receive:
      </p>
      <DS.Flex gap={3} mt={2}>
        <CheckItem>The system configuration you built: panel count, battery, payment type</CheckItem>
        <CheckItem>Your address and the energy inputs behind your estimate</CheckItem>
        <CheckItem>Your verified contact information</CheckItem>
      </DS.Flex>
    </Section>

    <PhaseSteps offset={AURORA_PHASE.steps.length} phase={INSTALLER_PHASE} />

    <Section align="center" bg="uiBgAlternate" pb={{ _: 7, lg: 7 }}>
      <H2>Why a software company is in your solar journey at all</H2>
      <DS.Text maxW="760px" mx="auto" text="body16">
        Aurora has built the design and sales software behind more than 20 million solar projects since 2013. We saw the
        same three problems hurt homeowners over and over, and the Marketplace is our answer to each.
      </DS.Text>
    </Section>
    <ColumnSection bg="uiBgAlternate" columnGap={7} columns={3} pt={{ _: 7, lg: 5 }} rowGap={7}>
      <DS.Flex flex={1} gap={4}>
        <DS.IconSolarPanel size={{ _: 6, md: 8 }} />
        <H3>Guesswork estimates</H3>
        <p>
          Ballpark quotes built to book a sales visit erode trust in the whole industry. We replace them with estimates
          modeled to your specific roof and bill, using technology independently validated by the National Renewable
          Energy Laboratory.
        </p>
      </DS.Flex>
      <DS.Flex flex={1} gap={4}>
        <DS.IconMoney size={{ _: 6, md: 8 }} />
        <H3>Prices you cannot compare</H3>
        <p>
          When every quote uses different assumptions, the loudest salesperson wins. Pricing the same system across
          every installer and every payment type puts the decision back on facts.
        </p>
      </DS.Flex>
      <DS.Flex flex={1} gap={4}>
        <DS.IconCheckCircle size={{ _: 6, md: 8 }} />
        <H3>Leads sold to the highest bidder</H3>
        <p>
          The standard lead-gen model sells your phone number to a list of companies. We invert it: you browse
          anonymously, and only the installer you pick ever hears from you.
        </p>
      </DS.Flex>
    </ColumnSection>

    <Section
      imageRight={{
        alt: 'Overhead view of a solar installation crew in hard hats reviewing plans together',
        height: 420,
        src: supportCrew,
      }}
    >
      <H2>Real people, the whole way through</H2>
      <p>
        Before, during, and after you connect with an installer, Aurora can help you understand your estimates, weigh
        your options, and navigate the process. We&rsquo;re available to support you by phone, through AI chat, or by
        text. This team works to find you the best energy solutions for your home and your situation.
      </p>
      <PhoneSupportCallout />
    </Section>

    <Section align="center" bg="uiButtonCta.defaultBgColor">
      <H2 id="get-estimates">Get estimates in minutes</H2>
      <x.p color="black" maxW="640px" mx="auto">
        Get free, personalized estimates and compare local installers, starting with just your address. No account, and
        no calls until you choose to connect in your own time and on your own terms.
      </x.p>
      <DS.Flex
        alignItems="center"
        flexDirection={{ _: 'column', md: 'row' }}
        gap={3}
        justifyContent="center"
        w="fit-content"
      >
        <GetEstimatesButton variant="primary">Get free estimates</GetEstimatesButton>
        <ButtonLink
          event={{ name: TRACKING.BLOG_READ_MORE }}
          path="/home-solar/faq"
          size={{ _: 'sm', md: 'md' }}
          variant="secondary"
        >
          Read the FAQ
        </ButtonLink>
      </DS.Flex>
    </Section>
  </article>
);
